import RemoveIcon from '@mui/icons-material/Remove';
import { IconButton, Menu, Tooltip, Rating as StarRating, Button } from '@mui/material';
import { EditIcon, UserIcon } from 'components/CustomIcons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import User from 'types/entities/User';
import { MissionAdvancementPossibilities } from 'types/enums/MissionAdvancementPossibilities';
import { Rating as RatingType } from 'types/enums/Rating';
import Rating from './Rating';

type OrderWorkUnitSummaryRowType = {
  id: number;
  name: string;
  workunits: OrderWorkunit[];
  accountable_id: number;
  accountable: User;
  ref_order: string;
  ref_work: string;
  order_scope_id: number;
  rating: string;
};

type RateComponentProps = {
  row?: OrderWorkunit | OrderWorkUnitSummaryRowType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRating: (orderWorkunit: any, rate: RatingType) => Promise<void>;
  disabled?: boolean;
  isPartial?: boolean;
};

const RateComponent = (props: RateComponentProps) => {
  const { row, setRating, disabled, isPartial = false } = props;
  const [value, setValue] = React.useState<number | null>(0);
  const [hover, setHover] = React.useState(-1);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  function isOrderWorkunit(arg: OrderWorkunit | OrderWorkUnitSummaryRowType): arg is OrderWorkunit {
    return Object.keys(arg).includes('mission_advancement_id');
  }

  const emptyCell = <RemoveIcon color="disabled" />;
  const getStringToMap = () => {
    if (row && isOrderWorkunit(row)) {
      if (row.mission_advancement_id === MissionAdvancementPossibilities.REFUSED_CLIENT) return [RatingType.E];
      if (row?.mission_advancement_id && row.mission_advancement_id >= MissionAdvancementPossibilities.RESERVE_CLIENT)
        return [RatingType.S];
    }

    return isPartial ? [RatingType.A, RatingType.B, RatingType.C] : [RatingType.S];
  };

  const starLabels: { [index: string]: string } = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
  };

  function getStarLabelText(value: number) {
    return `${value} Star${value !== 1 ? 's' : ''}, ${starLabels[value]}`;
  }

  const starRating = {
    A: 5,
    B: 4,
    C: 3,
    D: 2,
    E: 1,
    S: 0,
  };

  const stringsToMap = getStringToMap();
  const renderButton = () => {
    if (!disabled && row && !row.rating) {
      return <EditIcon fontSize="small" />;
    }
    if (row && row.rating && row.rating !== 'S') {
      return <StarRating value={starRating[row.rating as keyof typeof starRating] ?? null} readOnly size="small" />;
    }
    if (row && row.rating === 'S') {
      return (
        <Button
          variant="contained"
          color="primary"
          size="small"
          style={{ padding: '1px 10px', backgroundColor: '#2465dd' }}
        >
          S
        </Button>
      );
    }
    if ((!row || !row.rating) && disabled) {
      return emptyCell;
    }
    return emptyCell;
  };
  const [t] = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState('');
  const handleTooltip = (rate: any) => {
    setTooltipOpen(rate);
  };

  const getTooltip = (rating: string) => {
    if (rating === 'A') return t('5');
    if (rating === 'B') return t('4');
    if (rating === 'C') return t('3');
    if (rating === 'D') return t('2');
    if (rating === 'E') return t('1');
    if (rating === 'S') return t('Skip');
    return '';
  };

  return (
    <div style={{ maxWidth: '100px' }}>
      <Tooltip
        title={getTooltip(row?.rating as string) as string}
        key={`${row?.rating as string}`}
        open={tooltipOpen === (row?.rating as string)}
        placement="top"
        PopperProps={{ style: { pointerEvents: 'none', marginTop: '40px' } }}
      >
        <span
          onMouseEnter={() => {
            handleTooltip(row?.rating as string);
          }}
          onBlur={() => handleTooltip('')}
          onMouseLeave={() => handleTooltip('')}
        >
          <IconButton
            id="basic-button"
            aria-controls="basic-menu"
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            disabled={disabled}
            style={isPartial ? { border: '1px solid #dfd8d8', borderRadius: '0', marginLeft: '1rem' } : {}}
            size="large"
            color="primary"
          >
            {/* {row && row?.rating && !disabled && <Rating size="small" rating={row?.rating} />}
        {row && !row?.rating && !disabled && <RateReviewOutlined />}
        {disabled && emptyCell} */}
            {renderButton()}
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            style={{ zIndex: 14 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '2px' }}>
              <div className="MuiRating-root" style={{ display: 'flex', alignItems: 'center' }}>
                <StarRating
                  name="hover-feedback"
                  value={value}
                  precision={1}
                  size="large"
                  getLabelText={getStarLabelText}
                  onChange={(event, newValue) => {
                    const rating = newValue ?? 0;
                    setValue(rating);
                    setAnchorEl(null);
                    setRating(row as OrderWorkunit | OrderWorkUnitSummaryRowType, rating.toString() as RatingType);
                  }}
                  onChangeActive={(event, newHover) => {
                    const hover = newHover ?? 0;
                    setHover(hover);
                  }}
                />
              </div>

              {value !== null && (
                <span
                  style={{
                    backgroundColor: '#2465dd',
                    color: '#fff',
                    fontSize: 15,
                    padding: 6,
                    borderRadius: 6,
                    marginLeft: 4,
                  }}
                >
                  {value}
                </span>
              )}

              {stringsToMap.map((rate, index) => (
                <IconButton
                  disabled={disabled}
                  key={rate}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    const rating = 0;
                    setRating(row as OrderWorkunit | OrderWorkUnitSummaryRowType, rating.toString() as RatingType);
                    handleClose();
                  }}
                  size="large"
                >
                  <Rating size="small" rating={rate} open={open} handleTooltipParent={handleTooltip} />
                </IconButton>
              ))}
            </div>
          </Menu>
        </span>
      </Tooltip>
      {row?.rating && (row as OrderWorkunit).reviewer && !(row as OrderWorkunit).reviewer?.is_client && (
        <Tooltip
          title={t('assessed_dm') as string}
          placement="top"
          arrow
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                textAlign: 'center',
              },
            },
          }}
        >
          <div className="icon-container" style={{ position: 'relative', top: -10, left: 50, width: 15 }}>
            <UserIcon fontSize="small" />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
// eslint-disable-next-line react-hooks/exhaustive-deps
export default (props: RateComponentProps) => useMemo(() => <RateComponent {...props} />, [props.row, props.disabled]);
